import * as React from 'react';
import { Checkbox as FlowBiteCheckbox } from 'flowbite-react';
import { ControllerRenderProps } from 'react-hook-form';

import { useAuth } from '@restworld/feature-authentication';

import { cn } from '@restworld/utils-common';
import type { JSX } from 'react';
export const Checkbox: React.FunctionComponent<ICheckboxProps> = (props) => {
  const {
    title,
    isChecked,
    mode = 'clickable',
    id,
    onChange = () => null,
    onClick,
    variant = 'circle',
    value,
    field,
    iserror,
    background,
    disabled,
    testId,
    ...rest
  } = props;
  const { isAuthenticated } = useAuth();
  const checkboxEl = (
    <CheckboxStyled
      data-cy={testId}
      variant={variant}
      id={id}
      checked={isChecked}
      onChange={onChange}
      onClick={onClick}
      authenticated={isAuthenticated.toString()}
      value={value}
      iserror={iserror?.toString()}
      background={background}
      disabled={disabled}
      {...field}
      {...rest}
    />
  );
  return variant === 'chip' ? (
    <ChipDivWrapper isChecked={isChecked} mode={mode}>
      {checkboxEl}
      {title ? <ChippedLabelStyled isChecked={isChecked}>{title}</ChippedLabelStyled> : null}
    </ChipDivWrapper>
  ) : (
    <Wrapper
      disabled={disabled}
      ischecked={isChecked}
      background={background}
      htmlFor={id}
      authenticated={isAuthenticated}
      mode={mode}
    >
      {checkboxEl}
      {!!title && (typeof title === 'string' ? <LabelStyled>{title}</LabelStyled> : title)}
    </Wrapper>
  );
};
export default Checkbox;
const noPointerStyle = 'pointer-events-none cursor-auto';
const pointerStyle = 'pointer-events-auto cursor-pointer';
// --> styled components
const Wrapper = ({ mode, background, ischecked, disabled, className, ...props }: any) => (
  <label
    className={cn(
      'flex items-center gap-2.5 text-sm text-gray-600 font-normal',
      mode === 'static' ? noPointerStyle : pointerStyle,
      background === 'primary' ? 'bg-primary-50 p-4 rounded-lg' : '',
      background && ischecked ? 'border border-primary-700 bg-primary-100' : '',
      disabled ? 'pointer-events-none cursor-not-allowed saturate-25 opacity-60' : 'pointer-events-auto cursor-pointer',
      className
    )}
    {...props}
  />
);
const CheckboxStyled = ({ variant, authenticated, iserror, className, ...props }: any) => {
  const variantClasses = {
    circle: cn(
      'checked:!bg-primary-700 !rounded-3xl inline-block p-0 bg-contain',
      authenticated === 'true'
        ? "bg-[url('https://worker.restworld.it/utils-assets/checkbox-uncheck-cross.svg')]"
        : "bg-[url('https://worker.restworld.it/utils-assets/checkbox-uncheck-minus.svg')]"
    ),
    box: 'checked:!bg-primary-700 bg-white rounded',
    chip: cn(
      'focus:ring-primary-700',
      '!rounded-3xl inline-block p-0',
      "bg-[url('https://worker.restworld.it/utils-assets/checkbox-uncheck-minus.svg')] w-[20px] h-[20px] bg-no-repeat checked:bg-[url('https://worker.restworld.it/utils-assets/check-circle.svg')] bg-primary-800"
    ),
  };
  return (
    <FlowBiteCheckbox
      className={cn(
        'focus:ring-primary-700 border-primary-200 p-2',
        variantClasses[variant as keyof typeof variantClasses],
        iserror === 'true' && 'border-red-500',
        className
      )}
      {...props}
    />
  );
};
const LabelStyled = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-base text-gray-600 font-normal cursor-pointer', className)} {...props} />
);
const ChippedLabelStyled = ({ isChecked, className, ...props }: any) => (
  <span className={cn('text-sm font-normal', isChecked ? 'text-white' : 'text-primary-900', className)} {...props} />
);
const ChipDivWrapper = ({ isChecked, mode, className, ...props }: any) => (
  <label
    className={cn(
      'px-4 py-3 flex items-center gap-4 rounded-md w-fit h-[3rem]',
      isChecked ? 'bg-primary-800' : 'bg-white',
      mode === 'static' ? noPointerStyle : pointerStyle,
      className
    )}
    {...props}
  />
);
type CheckboxMode = 'static' | 'clickable';
export interface ICheckboxProps {
  title?: string | JSX.Element;
  isChecked?: boolean;
  mode?: CheckboxMode;
  id?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  variant?: CheckBoxVariantType;
  value?: string | boolean;
  field?: ControllerRenderProps;
  iserror?: boolean;
  background?: 'primary';
  disabled?: boolean;
  testId?: string;
}
export type CheckBoxVariantType = 'circle' | 'box' | 'chip';
